import React from 'react';

const POPOVER_GAP = 8;

export interface UseAnchoredPopoverOptions {
  popoverWidth?: number;
  minSpaceBelow?: number;
}

export interface UseAnchoredPopoverResult {
  rootRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  popoverRef: React.RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  popoverStyle: React.CSSProperties | null;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

const buildPopoverStyle = (
  trigger: HTMLElement,
  popoverWidth: number,
  minSpaceBelow: number,
): React.CSSProperties => {
  const rect = trigger.getBoundingClientRect();
  const width = Math.min(popoverWidth, window.innerWidth - 32);
  const left = Math.min(
    Math.max(16, rect.left),
    window.innerWidth - width - 16,
  );
  const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP;
  const spaceAbove = rect.top - POPOVER_GAP;
  const openUpward = spaceBelow < minSpaceBelow && spaceAbove > spaceBelow;

  return {
    position: 'fixed',
    top: openUpward ? undefined : rect.bottom + POPOVER_GAP,
    bottom: openUpward ? window.innerHeight - rect.top + POPOVER_GAP : undefined,
    left,
    width,
    zIndex: 1200,
  };
};

const popoverStylesEqual = (left: React.CSSProperties, right: React.CSSProperties): boolean =>
  left.top === right.top
  && left.bottom === right.bottom
  && left.left === right.left
  && left.width === right.width;

export const useAnchoredPopover = ({
  popoverWidth = 280,
  minSpaceBelow = 280,
}: UseAnchoredPopoverOptions = {}): UseAnchoredPopoverResult => {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [popoverStyle, setPopoverStyle] = React.useState<React.CSSProperties | null>(null);

  const close = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const updatePopoverPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const nextStyle = buildPopoverStyle(trigger, popoverWidth, minSpaceBelow);
    setPopoverStyle(current => (current && popoverStylesEqual(current, nextStyle) ? current : nextStyle));
  }, [minSpaceBelow, popoverWidth]);

  const open = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    setPopoverStyle(buildPopoverStyle(trigger, popoverWidth, minSpaceBelow));
    setIsOpen(true);
  }, [minSpaceBelow, popoverWidth]);

  const toggle = React.useCallback(() => {
    if (isOpen) {
      close();
      return;
    }

    open();
  }, [close, isOpen, open]);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePopoverPosition();
  }, [isOpen, updatePopoverPosition]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }

      close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    const handleReposition = () => updatePopoverPosition();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [close, isOpen, updatePopoverPosition]);

  return {
    rootRef,
    triggerRef,
    popoverRef,
    isOpen,
    popoverStyle,
    toggle,
    open,
    close,
  };
};
